import React from "react";

import UpgradeIcon from "@uui/Rocket02";
import { DateTime } from "luxon";

import Typography from "@mui/material/Typography";

import BillingLinkButton from "@app/analytics/account/Billing/components/BillingLinkButton";
import BillingModuleError from "@app/analytics/account/Billing/components/BillingModuleError";
import {
  BillingCard,
  BillingCardActions,
  BillingCardContent,
  BillingCardHeader,
} from "@app/analytics/account/Billing/components/styles";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import ROUTES from "@app/common/routes";
import { pluralizePhrase } from "@app/common/utils";

import IconBackground from "@rui/IconBackground";
import { Link } from "@rui/Link";
import { Row, Column } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

const CREDITS_TOOLTIP = {
  monthly: (plan: string, allotment: number) =>
    `On the ${plan} plan, you get ${allotment} minutes in free monthly credits. This resets on the first of each month.`,
  monthlyNoCredits:
    "Upgrade to Read Pro or Enterprise to get free monthly credits",
  purchased:
    "These are credits that you've purchased, in addition to your monthly credits.",
};

const VideoUploadCredits = ({ canPurchase }: { canPurchase: boolean }) => {
  const { currentPlan, subscription, uploadCredits, subscriptionIsError } =
    useBillingContext();

  const totalMonthlyCreditAllotment =
    subscription.settings.monthlyFreeUploadCredits;
  const remainingMonthlyCredits = uploadCredits.monthlyBalance;
  const remainingPurchasedCredits = uploadCredits.purchasedBalance;

  const monthlyCreditResetDate = DateTime.now()
    .startOf("month")
    .plus({ month: 1 })
    .toLocaleString({
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const hasMonthlyCreditAllowance =
    subscription.settings.monthlyFreeUploadCredits > 0;

  return (
    <BillingCard>
      <BillingCardHeader>
        <Typography variant="bodyPrimaryMed">Video Upload Credits</Typography>
      </BillingCardHeader>
      <BillingCardContent>
        {subscriptionIsError ? (
          <BillingModuleError entityLabel="video upload credits" />
        ) : (
          <>
            <Row
              variant="left"
              spacing={2}
              sx={{ color: "shades.neutral.800" }}
            >
              <Column flexBasis="50%" spacing={0.5} sx={{ height: "100%" }}>
                <Row variant="left" spacing={1}>
                  <Typography>Monthly credits</Typography>
                  <TooltipIcon
                    placement="bottom"
                    title={
                      hasMonthlyCreditAllowance
                        ? CREDITS_TOOLTIP.monthly(
                            currentPlan.label!,
                            totalMonthlyCreditAllotment
                          )
                        : CREDITS_TOOLTIP.monthlyNoCredits
                    }
                  />
                </Row>
                <Typography variant="h3">
                  {pluralizePhrase(remainingMonthlyCredits, "minute")}
                </Typography>
                <Row
                  variant="left"
                  spacing={1}
                  marginY={1.5}
                  sx={{ color: "shades.neutral.600" }}
                >
                  {hasMonthlyCreditAllowance ? (
                    <Typography variant="bodySecondaryReg">
                      Resets {monthlyCreditResetDate}
                    </Typography>
                  ) : (
                    <>
                      <IconBackground
                        height={24}
                        borderRadius="8px"
                        sx={{
                          color: "shades.primary.500",
                          bgcolor: "shades.ghost.500",
                        }}
                      >
                        <UpgradeIcon />
                      </IconBackground>
                      <Typography variant="labelReg">
                        <Link to={ROUTES.BILLING_COMPARE_PLANS}>
                          Upgrade your plan
                        </Link>{" "}
                        to get free monthly upload credits
                      </Typography>
                    </>
                  )}
                </Row>
              </Column>
              <Column flexBasis="50%" spacing={0.5} sx={{ height: "100%" }}>
                <Row variant="left" spacing={1}>
                  <Typography>Purchased credits</Typography>
                  <TooltipIcon
                    placement="bottom"
                    title={CREDITS_TOOLTIP.purchased}
                  />
                </Row>
                <Typography variant="h3">
                  {pluralizePhrase(remainingPurchasedCredits, "minute")}
                </Typography>
                {remainingPurchasedCredits > 0 && (
                  <Typography
                    variant="bodySecondaryReg"
                    sx={{ color: "shades.neutral.600", my: 1.5 }}
                  >
                    No expiration
                  </Typography>
                )}
              </Column>
            </Row>
            {canPurchase && (
              <BillingCardActions>
                <BillingLinkButton
                  to={ROUTES.BILLING_CHECKOUT_CREDITS}
                  openInBrowser
                >
                  Buy upload credits
                </BillingLinkButton>
              </BillingCardActions>
            )}
          </>
        )}
      </BillingCardContent>
    </BillingCard>
  );
};

export default VideoUploadCredits;
